import "./TasksBoard.css";

import { useContext } from "react";

import { ArrowFullLeftDarkIcon, RoundedPlusSignIcon } from "../../../../../assets";
import {
  OrganisationTypeConstants,
  ResourcePermissionConstants,
  WorkflowOrganisationAccessLevel,
} from "../../../../../constants";
import { ProjectPhase } from "../../../../../constants/project-phase";
import { TaskConstants } from "../../../../../constants/workflow/TaskConstants";
import { ProjectDates, ProjectPhaseStatus, ToggleTabsList } from "../../../../../models";
import { AssetManagerTemplate, DeveloperTemplate, EditDatesPanel, EditDealDatesPanel } from "../../../../../templates";
import { TasksGeneralView, useTasksGeneralView } from "../../../../../templates/TasksGeneralView";
import { UpcomingMilestonesTaskPanel } from "../../../../../templates/UpcomingMilestonesTaskPanel";
import { WorkflowTaskPanel } from "../../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../../useAuth";
import { stringKeyToSentenceCase } from "../../../../../utils";
import {
  Button,
  Column,
  ProfileMenu,
  ProfileMenuItem,
  QuickActionsMenu,
  Row,
  TasksProgressKPI,
  Toggle,
  ToggleTabs,
  UpcomingMilestones,
  UserHeader,
} from "../../../../../widget";
import { DealStagePill } from "../../../../asset-manager/deal/components";
import {
  AddStepModal,
  CompletePhaseModal,
  CompleteStageModal,
  DealTasksBoardBanner,
  NextPreviousPhaseButtons,
  ProjectTasksBoardBanner,
  RemoveWorkflowModal,
  WorkflowPermissionsPanel,
  WorkflowSteps,
} from "./components";
import { TasksBoardContext } from "./TasksBoardContext";
import { useTasksBoard } from "./useTasksBoard";

/*
  Header view switch: "Workflow" shows the phase/step board, "General" shows the combined task grid
  for this board's project (or deal). The segmented control sits in the header action group, left of
  Quick actions.
*/
const BOARD_VIEW_TAB_WORKFLOW = "Workflow";
const BOARD_VIEW_TAB_GENERAL = "General";
const boardViewTabs: ToggleTabsList = [{ label: BOARD_VIEW_TAB_WORKFLOW }, { label: BOARD_VIEW_TAB_GENERAL }];

const getProjectPhaseStatus = (phase: string, phaseDates: ProjectDates | undefined): string | null => {
  switch (phase) {
    case ProjectPhase.feasibility:
      return phaseDates?.feasibility?.status ?? ProjectPhaseStatus.NOT_STARTED;
    case ProjectPhase.design:
      return phaseDates?.design?.status ?? ProjectPhaseStatus.NOT_STARTED;
    case ProjectPhase.implementation:
      return phaseDates?.implementation?.status ?? ProjectPhaseStatus.NOT_STARTED;
    case ProjectPhase.ongoingMonitoring:
      return phaseDates?.ongoingMonitoring?.overallStatus ?? ProjectPhaseStatus.NOT_STARTED;
    default:
      return null;
  }
};

const getProjectPhaseStages = (
  phaseDates: ProjectDates | undefined,
  orderedPhases: string[]
): { key: string; label: string; status?: string | null }[] =>
  orderedPhases.map((phase) => ({
    key: phase,
    label: stringKeyToSentenceCase(phase),
    status: getProjectPhaseStatus(phase, phaseDates),
  }));

const TasksBoard = (): JSX.Element => {
  const { currentUserType, hasPermission } = useAuth();
  const {
    tasksBoard,
    objectName,
    quickActionsMenuOptionsList,
    showQuickActionsMenu,
    workflowOrganisations,
    workflow,

    showComplete,
    handleShowCompleteChange,

    showAddWorkflowStepPanel,
    setShowAddWorkflowStepPanel,

    showRemoveWorkflowModal,
    setShowRemoveWorkflowModal,

    showEditDatesPanel,
    closeEditDatesPanel,
    handleDealDatesSaved,

    isLoadingMilestones,
    hasMilestonesError,
    showUpcomingMilestonesPanel,
    setShowUpcomingMilestonesPanel,

    showWorkflowTaskPanel,
    setShowWorkflowTaskPanel,

    selectedPanelTask,
    setSelectedPanelTask,

    projectDates,
    setProjectDates,

    refreshTasksBoard,
    shouldRefreshTasksBoard,

    handleCloseWorkflowTaskPanel,
    setShowQuickActionsMenu,
    hasAccessLevelAndCanEditTemplate,

    handleBackClick,
    goBackText,
    upcomingMilestones,

    toggleProgress,
    taskProgress,
    onToggle,
    showWorkflowPermissionsPanel,
    setShowWorkflowPermissionsPanel,
    isDealWorkflow,
    dealStages,
    currentDealStage,
    handleDealStageChange,
  } = useTasksBoard();
  const {
    currentPhase,
    deal,
    handlePhaseChange,
    workflowStageOptions,
    workflowStageLabel,
    isRepeatableWorkflowStage,
    workflowTemplatePhases,
    boardView,
    setBoardView,
  } = useContext(TasksBoardContext);

  /*
    The object this board belongs to — general tasks are linked to it directly via objectUuid. This
    mirrors the board's own identity (useTasksBoard: dealUuid ?? projectUuid), so the project wins over
    the group it sits in: the General view must list the project's general tasks, not the landholding's.
  */
  const boardObjectUuid = workflow?.dealUuid ?? workflow?.projectUuid ?? workflow?.groupUuid ?? null;

  /*
    General task grid, shown when the "General" view is selected. General tasks have no workflow step and
    hang off the board's object through objectType/objectUuid, so the grid is scoped to that object alone —
    the workflow's own stepped tasks belong to the Workflow view.
  */
  const generalView = useTasksGeneralView({
    scopeFilterCriteria: boardObjectUuid ? { objectUuid: { operator: "eq", value: boardObjectUuid } } : undefined,
    statusFilter: showComplete ? undefined : { operator: "nin", value: [TaskConstants.TASK_STATUS_COMPLETE] },
    // The Workflow view resolves ?taskUuid= through useTasksBoard, so this grid owns the link only
    // while the General view is the one on screen.
    deepLinkEnabled: boardView === "general",
  });

  /*
    The two portals draw the view switch differently: the seed board (Figma 794:12837) uses a pill sized
    to its labels, the hub board (Figma 806:6574) a 216px squared control of two equal segments.
  */
  const isSeedPortal = currentUserType === OrganisationTypeConstants.ASSET_MANAGER;

  /*
    A general task is not part of the workflow, so its creation turns on the org-level createResource gate
    the API asserts rather than the workflow access level — a Viewer who can read this board must not be
    offered an Add task form the API would then reject.
  */
  const canCreateGeneralTask = hasPermission(ResourcePermissionConstants.CREATE_RESOURCE);

  const orderedProjectPhases =
    workflowTemplatePhases.length > 0
      ? workflowTemplatePhases.map((item) => item.phase)
      : (Object.values(ProjectPhase) as string[]);
  const projectPhaseStages = getProjectPhaseStages(projectDates?.dates, orderedProjectPhases);
  const selectedDealStageLabel =
    dealStages.find((stage) => stage.key === currentDealStage)?.label ??
    (currentDealStage ? stringKeyToSentenceCase(currentDealStage) : (dealStages[0]?.label ?? ""));

  const content = (
    <div className="TasksBoardContainer">
      <Row spacingV="ml">
        <Column span={12}>
          {currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? (
            <div className="HeaderContainer">
              <Button
                text={goBackText}
                variant="secondary"
                icon={<ArrowFullLeftDarkIcon />}
                iconPosition="left"
                onClick={handleBackClick}
                hasNoPadding
                minWidth={false}
              />
            </div>
          ) : (
            <UserHeader backButtonText={goBackText} handleBackClick={handleBackClick} />
          )}
        </Column>
      </Row>
      <Row spacingV="ml" justify="between">
        <Column span={12}>
          <div className="TasksBoardRow">
            <h1>{`${objectName} tasks board`}</h1>
            <div className={`TasksBoardHeaderActions${isSeedPortal ? " TasksBoardHeaderActions_seed" : ""}`}>
              <div className={`TasksBoardViewToggle${isSeedPortal ? " TasksBoardViewToggle_pill" : ""}`}>
                <ToggleTabs
                  toggleTabsList={boardViewTabs}
                  currentTab={boardView === "general" ? BOARD_VIEW_TAB_GENERAL : BOARD_VIEW_TAB_WORKFLOW}
                  onChange={(label) => setBoardView(label === BOARD_VIEW_TAB_GENERAL ? "general" : "workflow")}
                  variant={isSeedPortal ? "default" : "roundedSquare"}
                  size={isSeedPortal ? "small" : "medium"}
                />
              </div>
              {quickActionsMenuOptionsList.length > 0 && (
                <div>
                  <QuickActionsMenu onClick={() => setShowQuickActionsMenu(true)} />
                  <ProfileMenu
                    show={showQuickActionsMenu}
                    onClose={() => setShowQuickActionsMenu(false)}
                    position={{ top: "0" }}
                  >
                    {quickActionsMenuOptionsList.map((el) => (
                      <ProfileMenuItem
                        key={el.id}
                        profileMenuItem={el}
                        onClick={() => {
                          if (el.action) {
                            el.action();
                          }
                          setShowQuickActionsMenu(false);
                        }}
                      />
                    ))}
                  </ProfileMenu>
                </div>
              )}
            </div>
          </div>
        </Column>
      </Row>
      <Row spacingV="xl">
        <Column span={12}>
          <p className="body1">{`Manage the workflow steps and tasks for your chosen ${isDealWorkflow ? "deal" : "project"}`}</p>
        </Column>
      </Row>
      {boardView === "workflow" && (
        <>
          {/* Progress and milestones belong to the workflow board only — the General view is just the grid. */}
          <Row spacingV="ll">
            <Column span="6_half">
              {taskProgress && (
                <TasksProgressKPI data={taskProgress} toggleProgress={toggleProgress} onToggle={onToggle} />
              )}
            </Column>
            <Column span="5_half">
              <UpcomingMilestones
                milestones={upcomingMilestones}
                onViewAll={() => setShowUpcomingMilestonesPanel(true)}
                isLoading={isLoadingMilestones}
                hasError={hasMilestonesError}
                loadingCardCount={3}
              />
            </Column>
          </Row>
          <Row spacingV="ml">
            <Column span={12}>
              <div className="TasksBoardRow">
                {isDealWorkflow ? (
                  <div className="TasksBoardDealStageSelectorContainer">
                    <h3>Select stage</h3>
                    <DealStagePill
                      label={selectedDealStageLabel}
                      selectedStageLabel={currentDealStage}
                      stages={dealStages}
                      onStageClick={(stage) => handleDealStageChange(stage.key)}
                    />
                  </div>
                ) : (
                  <div className="TasksBoardDealStageSelectorContainer">
                    <h3>Select phase</h3>
                    <DealStagePill
                      label={stringKeyToSentenceCase(currentPhase)}
                      selectedStageLabel={currentPhase}
                      stages={projectPhaseStages}
                      onStageClick={(stage) => handlePhaseChange(stage.key as ProjectPhase)}
                    />
                  </div>
                )}

                <div className="TasksBoardRowRight">
                  <div className="TasksBoardRowRightToggleWrapper">
                    <Toggle
                      label={`${showComplete ? "Hide" : "Show"} complete`}
                      labelAlignment="left"
                      value={showComplete}
                      onChange={() => handleShowCompleteChange()}
                      variant="primary"
                    />
                  </div>
                  {hasAccessLevelAndCanEditTemplate(WorkflowOrganisationAccessLevel.Contributor) && (
                    <Button
                      text="Add step"
                      variant="primary"
                      hasNoPadding
                      minWidth={false}
                      size="small"
                      icon={<RoundedPlusSignIcon className="RoundedPlusSignIcon" />}
                      onClick={() => setShowAddWorkflowStepPanel(true)}
                    />
                  )}
                </div>
              </div>
            </Column>
          </Row>
          {isDealWorkflow ? <DealTasksBoardBanner /> : <ProjectTasksBoardBanner />}
          <Row spacingV="ml">
            <Column span={12}>
              <WorkflowSteps handleAddWorkflowStep={() => setShowAddWorkflowStepPanel(true)} />
            </Column>
          </Row>
          {!isDealWorkflow && (
            <Row spacingV="ll">
              <Column span={12}>
                <NextPreviousPhaseButtons
                  currentPhase={currentPhase}
                  handlePhaseChange={handlePhaseChange}
                  phaseOrder={orderedProjectPhases as ProjectPhase[]}
                />
              </Column>
            </Row>
          )}
        </>
      )}
      {boardView === "general" && (
        <Row spacingV="ml">
          <Column span={12}>
            {/* The grid sits in its own white card (Figma seed 794:12846, hub 806:9009). */}
            <div className={`TasksBoardGeneralViewCard${isSeedPortal ? " TasksBoardGeneralViewCard_seed" : ""}`}>
              <TasksGeneralView
                showAddTask={canCreateGeneralTask}
                columns={generalView.columns}
                onChange={generalView.onChange}
                defaultSortingCriteria={generalView.defaultSortingCriteria}
                dataIsLoading={generalView.dataIsLoading}
                hasData={generalView.hasData}
                refresh={generalView.refresh}
                handleRefresh={generalView.handleRefresh}
                tagsFilterSelectData={generalView.tagsFilterSelectData}
                tagsFilterValues={generalView.tagsFilterValues}
                onTagsFilter={generalView.onTagsFilter}
                showWorkflowTaskPanel={generalView.showWorkflowTaskPanel}
                selectedPanelTask={generalView.selectedPanelTask}
                handleAddTask={generalView.handleAddTask}
                handleCloseWorkflowTaskPanel={generalView.handleCloseWorkflowTaskPanel}
                workflow={workflow}
              />
            </div>
          </Column>
        </Row>
      )}
      <AddStepModal
        show={showAddWorkflowStepPanel}
        closeModal={() => setShowAddWorkflowStepPanel(false)}
        workflowUuid={tasksBoard?.results[0].uuid}
      />
      <RemoveWorkflowModal show={showRemoveWorkflowModal} closeModal={() => setShowRemoveWorkflowModal(false)} />
      {!isDealWorkflow && <CompletePhaseModal />}
      {isDealWorkflow && <CompleteStageModal />}
      {projectDates && (
        <EditDatesPanel
          data={projectDates}
          setData={setProjectDates}
          show={showEditDatesPanel}
          onClose={closeEditDatesPanel}
        />
      )}
      {isDealWorkflow && (
        <EditDealDatesPanel
          deal={deal}
          workflowTemplatePhases={workflowTemplatePhases}
          show={showEditDatesPanel}
          onClose={closeEditDatesPanel}
          onSaved={handleDealDatesSaved}
        />
      )}
      <WorkflowPermissionsPanel
        workflowUuid={tasksBoard?.results[0].uuid}
        projectUuid={tasksBoard?.results[0].projectUuid ?? undefined}
        onClose={() => setShowWorkflowPermissionsPanel(false)}
        show={showWorkflowPermissionsPanel}
        setShow={setShowWorkflowPermissionsPanel}
        existingWorkflowOrganisations={workflowOrganisations}
      />
      <UpcomingMilestonesTaskPanel
        workflowUuid={workflow?.uuid ?? undefined}
        projectUuid={workflow?.projectUuid ?? undefined}
        show={showUpcomingMilestonesPanel}
        onClose={() => setShowUpcomingMilestonesPanel(false)}
        setSelectedPanelTask={setSelectedPanelTask}
        setShowWorkflowTaskPanel={setShowWorkflowTaskPanel}
        refreshTasks={refreshTasksBoard}
      />
      {selectedPanelTask && (
        <WorkflowTaskPanel
          task={selectedPanelTask}
          currentStepUuid={selectedPanelTask?.step?.uuid}
          workflow={workflow}
          show={showWorkflowTaskPanel}
          phaseOptions={workflowStageOptions}
          phaseLabel={workflowStageLabel}
          isRepeatablePhase={isRepeatableWorkflowStage}
          refresh={refreshTasksBoard}
          handleRefresh={shouldRefreshTasksBoard}
          onClose={handleCloseWorkflowTaskPanel}
        />
      )}
    </div>
  );

  if (currentUserType === OrganisationTypeConstants.ASSET_MANAGER) {
    return <AssetManagerTemplate activeElement="Operations">{content}</AssetManagerTemplate>;
  }

  return <DeveloperTemplate activeElement="Tasks">{content}</DeveloperTemplate>;
};

export default TasksBoard;
